import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { TextInput, View } from 'react-native'
import { useSelector } from 'react-redux'

import { isEmpty } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import LoadingIndicator from '@lyrahealth-inc/ui-core/lib/atoms/loadingIndicator/LoadingIndicator'
import { MedicineIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/MedicineIcon'
import { SearchIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SearchIcon'
import { XIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/XIcon'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { UnstyledIconButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/unstyledIconButton/UnstyledIconButton'
import MedicationsList from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/medicationsList/MedicationsList'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { useGetPatientPrescriptionsQuery, useLazyGetDSPatientUrlQuery } from './data/medicationsApi'
import DateFilterDropdown, { DateRange } from './DateFilterDropdown'
import styles from './medications.module.scss'
import StatusFilter from './StatusFilter'
import { programConfig, ROLES } from '../../common/constants/appConstants'
import { hasRole } from '../../common/utils/utils'
import { getAuthConfig, getAuthRoles } from '../../data/auth/authSelectors'
import { getSelectedClientId } from '../../data/lyraTherapy/clientSelectors'
import { useAssignmentsData } from '../assignments/data/hooks/useAssignmentsData'
import MedicationsPDF from '../clients/clientDetails/clientRecords/MedicationsPDF'

// Custom search input styled to match Figma design
const SearchInputContainer = styled.View(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  borderWidth: 1,
  borderColor: theme.colors.borderDefault,
  borderRadius: 8,
  flexDirection: 'row',
  alignItems: 'center',
  paddingHorizontal: theme.spacing['16px'],
  paddingVertical: theme.spacing['12px'],
  height: 48,
  width: 249,
}))

const SearchInput = styled(TextInput)(({ theme }) => ({
  flex: 1,
  fontSize: 16,
  color: theme.colors.textPrimary,
  backgroundColor: 'transparent',
  marginLeft: theme.spacing['8px'],
}))

const SearchIconWrapper = styled.View({
  width: 24,
  height: 24,
  justifyContent: 'center',
  alignItems: 'center',
})

const FilterRow = styled.View(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  marginBottom: theme.spacing['16px'],
  zIndex: 10,
}))

const LeftFiltersContainer = styled.View(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-end',
  gap: theme.spacing['8px'],
  flex: 1,
}))

const calculateInitialDateRange = (): DateRange | null => {
  const endDate = new Date()
  endDate.setDate(endDate.getDate() + 1)
  const startDate = new Date()
  startDate.setMonth(startDate.getMonth() - 3)
  return { start: startDate, end: endDate }
}

const RightFiltersContainer = styled.View({
  alignItems: 'flex-end',
})

const PrescribeContainer = styled.View(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  marginLeft: theme.spacing['8px'],
}))

export enum FILTER_TYPE {
  STATUS = 'status',
  DATE_RANGE = 'dateRange',
}

export const Medications: React.FC<MedicationsProps> = ({ setMedicationsPDFContent }) => {
  const clientId = useSelector(getSelectedClientId)
  const [searchTerm, setSearchTerm] = useState('')
  const [dateRange, setDateRange] = useState<DateRange | null>(calculateInitialDateRange())
  const [selectedStatus, setSelectedStatus] = useState<string>('All')
  const intl = useIntl()
  const { colors } = useTheme()
  const userRoles: string[] = useSelector(getAuthRoles)
  const config = useSelector(getAuthConfig)
  const [loadingDSUrl, setLoadingDSUrl] = useState(false)
  const [isPDFContentInitialized, setIsPDFContentInitialized] = React.useState(false)
  const [activeFilter, setActiveFilter] = useState<FILTER_TYPE | null>(null)
  const formatDateForAPI = (date: Date): string => {
    return date.toISOString().split('T')[0]
  }

  const isPrescriber = hasRole(userRoles, [ROLES.LT_PRESCRIBER])

  const { data: assignments } = useAssignmentsData()
  const clientIntake = assignments?.find(
    (assignment) =>
      [
        programConfig.MedicationManagement.clientIntake,
        programConfig.MedicationManagement.oldClientIntake,
        programConfig.ClinicalLeaveEvaluation.clientIntake,
        programConfig.DialecticalBehaviorTherapy.clientIntake,
        programConfig.DialecticalBehaviorTherapyTeens.clientIntake,
      ].includes(assignment.content.name) && assignment.status === 'completed',
  )

  const { data: prescriptions = [], isLoading: loadingPrescriptions } = useGetPatientPrescriptionsQuery(
    {
      clientId: clientId!,
      ...(dateRange && {
        startDate: formatDateForAPI(dateRange.start),
        endDate: formatDateForAPI(dateRange.end),
      }),
    },
    {
      skip: !clientId || !clientIntake || !config?.medicationEnabled,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    },
  )

  const [getDSPatientUrl] = useLazyGetDSPatientUrlQuery()

  const fetchDSUrl = async () => {
    if (!clientId) {
      return
    }
    try {
      setLoadingDSUrl(true)
      const result = await getDSPatientUrl({ clientId }).unwrap()
      window.open(result.sso_url, '_blank')
    } finally {
      setLoadingDSUrl(false)
    }
  }

  const handleDateFilterChange = (selectedDateRange: DateRange) => {
    setDateRange(selectedDateRange)
  }

  const availableStatuses = useMemo(() => {
    const statusSet = new Set<string>()

    prescriptions
      .filter((prescription) => prescription.Status !== 8) // Exclude "requested" status
      .forEach((prescription) => {
        if (typeof prescription.MedicationStatus === 'string' && prescription.MedicationStatus.trim()) {
          statusSet.add(prescription.MedicationStatus.trim())
        }
      })

    const availableStatuses = Array.from(statusSet).sort()
    availableStatuses.unshift('All')
    return availableStatuses
  }, [prescriptions])

  // ICE-3160: Filtering out prescriptions where status is of requested
  const filteredPrescriptions = prescriptions
    .filter((prescriptionItem) => prescriptionItem.Status !== 8)
    .filter(
      (prescriptionItem) =>
        !searchTerm.trim() || prescriptionItem.DisplayName?.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .filter((prescriptionItem) => {
      if (!selectedStatus || selectedStatus === 'All') return true

      const medicationStatus =
        typeof prescriptionItem.MedicationStatus === 'string' ? prescriptionItem.MedicationStatus.trim() : ''

      return medicationStatus === selectedStatus
    })

  useEffect(() => {
    if (!config?.medicationEnabled || isPDFContentInitialized || !setMedicationsPDFContent) {
      return
    }
    const filteredPrescriptionsForPDF = prescriptions.filter((prescriptionItem) => prescriptionItem.Status !== 8)
    setMedicationsPDFContent(
      <MedicationsPDF
        medications={filteredPrescriptionsForPDF}
        clientIntake={clientIntake}
        isPrescriber={isPrescriber}
      />,
    )
    setIsPDFContentInitialized(true)
  }, [
    prescriptions,
    clientId,
    clientIntake,
    config?.medicationEnabled,
    isPDFContentInitialized,
    isPrescriber,
    setMedicationsPDFContent,
  ])
  if (!config?.medicationEnabled) {
    return null
  }

  return (
    <View>
      {(isEmpty(filteredPrescriptions) && loadingPrescriptions) || loadingDSUrl ? (
        <div className={styles['loading-container']}>
          <LoadingIndicator size={45} />
        </div>
      ) : (
        <>
          {clientIntake ? (
            <>
              <FilterRow>
                <LeftFiltersContainer>
                  <SearchInputContainer>
                    <SearchIconWrapper>
                      <SearchIcon fillColor={colors.iconDefault} />
                    </SearchIconWrapper>
                    <SearchInput
                      testID={tID('MedicationsList-search-input')}
                      placeholder={intl.formatMessage({
                        defaultMessage: 'Search medications',
                        description: 'Search medications by name',
                      })}
                      value={searchTerm}
                      onChangeText={setSearchTerm}
                      placeholderTextColor={colors.textSecondary}
                    />
                    {searchTerm ? (
                      <UnstyledIconButton
                        testID={tID('MedicationsList-clear-button')}
                        onPress={() => setSearchTerm('')}
                        accessibilityLabel={intl.formatMessage({
                          defaultMessage: 'Clear search',
                          description: 'Clear search button',
                        })}
                      >
                        <XIcon fillColor={colors.iconDefault} />
                      </UnstyledIconButton>
                    ) : null}
                  </SearchInputContainer>
                  {availableStatuses.length > 1 && (
                    <StatusFilter
                      statuses={availableStatuses}
                      onSelection={setSelectedStatus}
                      selectedStatus={selectedStatus}
                      testID={tID('MedicationsStatusFilter')}
                      activeFilter={activeFilter}
                      onFilterActive={() => setActiveFilter(FILTER_TYPE.STATUS)}
                    />
                  )}
                  <DateFilterDropdown
                    onSelectionChange={handleDateFilterChange}
                    testID={tID('MedicationsList-date-filter')}
                    defaultValue='last3months'
                    activeFilter={activeFilter}
                    onFilterActive={() => setActiveFilter(FILTER_TYPE.DATE_RANGE)}
                  />
                </LeftFiltersContainer>
                <RightFiltersContainer>
                  <PrescribeContainer>
                    {isPrescriber && (
                      <PrimaryButton
                        testID={tID('Medications-AddMedication')}
                        onPress={fetchDSUrl}
                        text={'Add Medication'}
                      />
                    )}
                  </PrescribeContainer>
                </RightFiltersContainer>
              </FilterRow>
              <MedicationsList medications={filteredPrescriptions} isPrescriber={isPrescriber} />
            </>
          ) : (
            <div className={styles['no-data-container']} data-test-id='Medications-noData'>
              <div className={styles['text-container']}>
                <h4 className={styles.title}>Intake form incomplete</h4>
                <p className={styles['missing-intake']} data-test-id='Medications-missing-intake'>
                  Your client hasn’t completed their client intake yet and therefore has not given consent.{' '}
                  {isPrescriber &&
                    'Encourage your client to complete this, as you will not be able to prescribe medication until they do so.'}
                </p>
              </div>
              <MedicineIcon width='100' />
            </div>
          )}
        </>
      )}
    </View>
  )
}

type MedicationsProps = {
  setMedicationsPDFContent?: (content: any) => void
}

export default Medications
